/**
 * Network layer — owns the WebSocket, sends outgoing messages, dispatches
 * incoming messages to a small set of typed listeners.
 *
 * Outgoing: thin wrappers (`sendName`, `sendMove`, `sendAttack`, `sendChat`,
 *           `sendPickup`).
 * Incoming: subscribe via `on(type, handler)` or pass a `MultiplayerEvents`
 *           object to `createMultiplayer`.
 *
 * The connection auto-reconnects with exponential backoff after a close.
 */

import type { WireMessage } from '@hurka/shared';

export interface MultiplayerEvents {
  onServerHello?: (msg: Extract<WireMessage, { type: 'server.hello' }>) => void;
  onWorldState?: (msg: Extract<WireMessage, { type: 'world.state' }>) => void;
  onPlayerJoined?: (msg: Extract<WireMessage, { type: 'player.joined' }>) => void;
  onPlayerLeft?: (msg: Extract<WireMessage, { type: 'player.left' }>) => void;
  onChat?: (msg: Extract<WireMessage, { type: 'chat.broadcast' }>) => void;
  onEnemyState?: (msg: Extract<WireMessage, { type: 'enemy.state' }>) => void;
  onEnemyDied?: (msg: Extract<WireMessage, { type: 'enemy.died' }>) => void;
  onCombatDamage?: (msg: Extract<WireMessage, { type: 'combat.damage' }>) => void;
  onLootSpawn?: (msg: Extract<WireMessage, { type: 'loot.spawn' }>) => void;
  onLootPickup?: (msg: Extract<WireMessage, { type: 'loot.pickup' }>) => void;
  onHouseEntered?: (msg: Extract<WireMessage, { type: 'house.entered' }>) => void;
  onHouseExited?: (msg: Extract<WireMessage, { type: 'house.exited' }>) => void;
  onPong?: (msg: Extract<WireMessage, { type: 'pong' }>) => void;
  onConnectionChange?: (state: ConnectionState) => void;
}

export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

export interface Multiplayer {
  sendName(name: string): void;
  sendMove(x: number, y: number, z: number, yaw: number): void;
  sendAttack(enemyId: string): void;
  sendChat(text: string): void;
  sendPickup(lootId: string): void;
  sendHouseEnter(houseId: string): void;
  sendHouseExit(): void;
  state(): ConnectionState;
  playerId(): string | null;
  dispose(): void;
}

export function createMultiplayer(events: MultiplayerEvents = {}): Multiplayer {
  let socket: WebSocket | null = null;
  let connState: ConnectionState = 'connecting';
  let myId: string | null = null;
  let retryMs = 1000;
  const MAX_RETRY = 8000;

  function setState(s: ConnectionState): void {
    connState = s;
    events.onConnectionChange?.(s);
  }

  function send(msg: WireMessage): void {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
  }

  function connect(): void {
    setState('connecting');
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/ws`;
    socket = new WebSocket(url);

    socket.onopen = () => {
      retryMs = 1000;
      setState('connected');
      send({ type: 'client.hello', clientTime: Date.now() });
    };

    socket.onmessage = (ev) => {
      let msg: WireMessage;
      try {
        msg = JSON.parse(ev.data) as WireMessage;
      } catch {
        return;
      }
      switch (msg.type) {
        case 'server.hello':
          myId = msg.playerId;
          events.onServerHello?.(msg);
          break;
        case 'world.state':
          events.onWorldState?.(msg);
          break;
        case 'player.joined':
          events.onPlayerJoined?.(msg);
          break;
        case 'player.left':
          events.onPlayerLeft?.(msg);
          break;
        case 'chat.broadcast':
          events.onChat?.(msg);
          break;
        case 'enemy.state':
          events.onEnemyState?.(msg);
          break;
        case 'enemy.died':
          events.onEnemyDied?.(msg);
          break;
        case 'combat.damage':
          events.onCombatDamage?.(msg);
          break;
        case 'loot.spawn':
          events.onLootSpawn?.(msg);
          break;
        case 'loot.pickup':
          events.onLootPickup?.(msg);
          break;
        case 'house.entered':
          events.onHouseEntered?.(msg);
          break;
        case 'house.exited':
          events.onHouseExited?.(msg);
          break;
        case 'pong':
          events.onPong?.(msg);
          break;
        default:
          break;
      }
    };

    socket.onclose = () => {
      setState('disconnected');
      setTimeout(connect, retryMs);
      retryMs = Math.min(MAX_RETRY, retryMs * 2);
    };

    socket.onerror = () => {
      // close handler will follow.
    };
  }

  connect();

  return {
    sendName(name) {
      send({ type: 'client.name', name });
    },
    sendMove(x, y, z, yaw) {
      send({ type: 'client.move', x, y, z, yaw });
    },
    sendAttack(enemyId) {
      send({ type: 'client.attack', enemyId, t: Date.now() });
    },
    sendChat(text) {
      send({ type: 'client.chat', text });
    },
    sendPickup(lootId) {
      send({ type: 'client.pickup', lootId });
    },
    sendHouseEnter(houseId) {
      send({ type: 'client.house.enter', houseId });
    },
    sendHouseExit() {
      send({ type: 'client.house.exit' });
    },
    state: () => connState,
    playerId: () => myId,
    dispose() {
      socket?.close();
    },
  };
}
